import { Component, computed, inject, signal } from '@angular/core';
import { BasePageComponent } from "../pages-base/base-page.component";
import {
  ReleaseCalendarData,
  ReleaseCalendarSelectedOptions
} from "./types/release-calendar-types";
import { MatButton, MatMiniFabButton } from "@angular/material/button";
import { DEFAULT_ZOOM } from "../../../core/constants/constants";
import { ReleaseCalendarService } from "./services/release-calendar.service";
import { MoveDirection } from "../../../core/types/move-direction.types";
import { arrowButtons } from "./configs/release-calendar-arrow-config";
import { Subscription } from "rxjs";
import { LoadingService } from "../../../core/services/loading.service";
import { DeviceService } from "../../../core/services/device.service";
import { smoothShowHideHeight, smoothShowHideWidth } from "../../../shared/animations/smoothShowHideAnimations";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import {
  ReleaseCalendarProductComponent
} from "./components/release-calendar-product/release-calendar-product.component";
import {
  ReleaseCalendarProductExpensesComponent
} from "./components/release-calendar-product-expenses/release-calendar-product-expenses.component";
import { ReleaseCalendarTeamComponent } from "./components/release-calendar-team/release-calendar-team.component";
import {
  ReleaseCalendarPageInfoComponent
} from "./components/release-calendar-page-info/release-calendar-page-info.component";
import { PageInfoComponent } from "../../../core/components/page-info/page-info.component";
import { releaseCalendarResourceType } from "./configs/release-calendar-config";

@Component({
  selector: 'app-release-calendar-page',
  standalone: true,
  imports: [
    MatButton,
    MatMiniFabButton,
    LoaderComponent,
    ReleaseCalendarProductComponent,
    ReleaseCalendarProductExpensesComponent,
    ReleaseCalendarTeamComponent,
    ReleaseCalendarPageInfoComponent,
    PageInfoComponent
  ],
  templateUrl: './release-calendar-page.component.html',
  styleUrl: './release-calendar-page.component.scss',
  animations: [ smoothShowHideHeight, smoothShowHideWidth ]
})
export class ReleaseCalendarPageComponent extends BasePageComponent {
  private releaseCalendarService: ReleaseCalendarService = inject(ReleaseCalendarService);
  private loadingService: LoadingService = inject(LoadingService);
  private deviceService: DeviceService = inject(DeviceService);

  public readonly releaseCalendarResourceType = releaseCalendarResourceType;
  public readonly arrowButtons = arrowButtons;
  public releaseCalendarData = signal<ReleaseCalendarData | null>(null);
  public releaseCalendarSelectedOptions = signal<ReleaseCalendarSelectedOptions | null>(null);
  public visibleGroups = signal<Array<boolean>>([]);
  public currentZoomOption = signal<number>(DEFAULT_ZOOM);
  public isLoading = signal<boolean>(false);
  public isMobile = signal<boolean>(false);
  public groupStyles = computed<Record<string, string>>(() =>
    this.releaseCalendarService.updateGroupStyles({
      currentZoomOption: this.currentZoomOption(),
      isMobile: this.isMobile()
    })
  );

  private subscriptions: Subscription = new Subscription();

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initPage();
  }

  public toggleGroupVisibility(productIndex: number): void {
    this.visibleGroups()[productIndex] = !this.visibleGroups()[productIndex];
  }

  public isNoVisibleGroup() {
    return !this.visibleGroups().some(isVisible => isVisible);
  }

  public handleArrowClick(productIndex: number, direction: MoveDirection): void {
    const data = this.releaseCalendarData();
    if (!data) { return; }

    const products = this.releaseCalendarService.updatePositionProducts({
      products: [ ...data.products ],
      direction: direction,
      productIndex: productIndex
    });

    this.releaseCalendarData.set({
      ...data,
      products: products
    });

    this.updateVisibleGroups();
  }

  private async initPage(): Promise<void> {
    this.isMobile.set(this.deviceService.isMobile);
    this.initSubscriptions();
    this.loadingService.setIsLoadingState(true);
    !this.releaseCalendarSelectedOptions() && this.releaseCalendarSelectedOptions.set(this.releaseCalendarService.getDefaultReleaseCalendarSelectedOptions());
    await this.loadReleaseCalendarData();
  }

  private initSubscriptions() {
    this.subscriptions.add(
      this.loadingService.loadingState$.subscribe((isLoading) => this.isLoading.set(isLoading))
    );
    this.subscriptions.add(
      this.deviceService.isMobile$.subscribe((isMobile) => this.isMobile.set(isMobile))
    );
  }

  private updateVisibleGroups() {
    if (!this.releaseCalendarData()?.products) { return; }

    const newVisibleGroups: Array<boolean> = [];
    this.releaseCalendarData()?.products.forEach((_, index) => {
      newVisibleGroups[index] = this.visibleGroups()[index];
    });
    this.visibleGroups.set(newVisibleGroups);
  }

  private async loadReleaseCalendarData(): Promise<void> {
    const queryParams = this.releaseCalendarService.getReleaseCalendarQueryParams(this.releaseCalendarSelectedOptions());
    this.releaseCalendarService.getReleaseCalendarData(queryParams)
      .then((data) => this.successLoadData(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private successLoadData(data: ReleaseCalendarData): void {
    this.releaseCalendarData.set(data);
    const products = this.releaseCalendarData()?.products;
    !!products?.length && this.visibleGroups.set(products.map(() => false));
  }
}
